// src/api/services/truckWeight.ts
import { api } from './index';

// TruckWeight interfaces
export interface TruckWeight {
  id: number;
  headLicensePlate: string;
  tailLicensePlate?: string;
  weight: number;
  dateTime: Date;
}

export interface TruckWeightCreateDTO {
  headLicensePlate: string;
  tailLicensePlate?: string;
  weight: number;
  dateTime: Date;
}

export interface TruckWeightUpdateDTO {
  headLicensePlate?: string;
  tailLicensePlate?: string;
  weight?: number;
  dateTime?: Date;
}

// Query options interface
export interface QueryOptions {
  max?: number;
  offset?: number;
  sort?: string;
  order?: 'ASC' | 'DESC';
  view?: string;
  additional?: string;
  [key: string]: any; // For custom filters
}

// Where query interface
export interface WhereQuery {
  where: string;
  params: Record<string, any>;
}

// Response interfaces
export interface ListResponse<T> {
  statusCode: number;
  data: T[];
  totalCount: number;
}

export interface SingleResponse<T> {
  statusCode: number;
  data: T;
}

// TruckWeight service
export const truckWeightService = {
  /**
   * List all truck weights with optional pagination and sorting
   */
  list: (options?: QueryOptions) =>
    api.get<ListResponse<TruckWeight>>('/truckWeight', { params: options }),

  /**
   * Find truck weights with complex filtering
   */
  where: (whereData: WhereQuery, options?: QueryOptions) =>
    api.post<ListResponse<TruckWeight>>('/truckWeight/where', whereData, { 
      params: options 
    }),

  /**
   * Get a single truck weight by ID
   */
  read: (id: number, options?: Pick<QueryOptions, 'view' | 'additional'>) =>
    api.get<SingleResponse<TruckWeight>>(`/truckWeight/${id}`, { 
      params: options 
    }),

  /**
   * Create a new truck weight record
   */
  create: (data: TruckWeightCreateDTO) =>
    api.post<SingleResponse<TruckWeight>>('/truckWeight', data),

  /**
   * Update an existing truck weight record
   */
  update: (id: number, data: TruckWeightUpdateDTO) =>
    api.put<SingleResponse<TruckWeight>>(`/truckWeight/${id}`, data),

  /**
   * Delete a truck weight record
   */
  delete: (id: number) =>
    api.delete<{ statusCode: number }>(`/truckWeight/${id}`),
  
  /**
   * Helper method to find truck weights by license plate
   */
  findByLicensePlate: (licensePlate: string, options?: QueryOptions) => {
    const whereData: WhereQuery = {
      where: "_.headLicensePlate = :licensePlate OR _.tailLicensePlate = :licensePlate",
      params: { licensePlate }
    };
    return truckWeightService.where(whereData, options);
  },

  /**
   * Helper method to find truck weights by date range
   */
  findByDateRange: (startDate: Date, endDate: Date, options?: QueryOptions) => {
    const whereData: WhereQuery = {
      where: "_.dateTime >= :startDate AND _.dateTime <= :endDate",
      params: { 
        startDate: startDate.toISOString(), 
        endDate: endDate.toISOString() 
      }
    };
    return truckWeightService.where(whereData, options);
  },

  /**
   * Helper method to find truck weights within a weight range
   */
  findByWeightRange: (minWeight: number, maxWeight: number, options?: QueryOptions) => {
    const whereData: WhereQuery = {
      where: "_.weight >= :minWeight AND _.weight <= :maxWeight",
      params: { minWeight, maxWeight }
    };
    return truckWeightService.where(whereData, options);
  }
};